import sanitizeHtml from "sanitize-html";

/** Kept for CSS-only interactivity; `form` is not allowed, so none can submit. */
const CSS_ONLY_INPUT_TYPES = new Set(["checkbox", "radio"]);

/**
 * Shared sanitizer for merchant-authored CUSTOM_HTML blocks: anything outside
 * the allowlist (scripts, iframes, forms, svg, event handlers) is dropped.
 */
export function sanitizeCustomHtml(html: string): string {
  return sanitizeHtml(html, {
    allowedTags: [
      "div",
      "span",
      "p",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "a",
      "img",
      "picture",
      "source",
      "nav",
      "section",
      "header",
      "footer",
      "main",
      "article",
      "aside",
      "table",
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "td",
      "th",
      "br",
      "hr",
      "strong",
      "em",
      "b",
      "i",
      "u",
      "s",
      "small",
      "sup",
      "sub",
      "blockquote",
      "figure",
      "figcaption",
      "details",
      "summary",
      // label + checkbox/radio inputs (exclusiveFilter strips other input
      // types) enable CSS-only menu toggles and swatch pickers.
      "label",
      "input",
      "style",
    ],
    // Page-global merchant CSS is an accepted pre-existing risk (portal
    // customization precedent); the flag is required to permit <style>.
    allowVulnerableTags: true,
    // Keeps style attributes verbatim instead of round-tripping them through
    // postcss, which needs Node builtins and breaks in browser bundles.
    parseStyleAttributes: false,
    allowedAttributes: {
      "*": ["class", "id", "style", "role", "title", "aria-*", "data-*"],
      a: ["href", "target", "rel", "name"],
      img: ["src", "srcset", "sizes", "alt", "width", "height", "loading"],
      source: ["src", "srcset", "sizes", "media", "type"],
      td: ["colspan", "rowspan"],
      th: ["colspan", "rowspan"],
      details: ["open"],
      label: ["for"],
      // `name` groups radios. It adds no clobbering surface beyond the already
      // allowed `id`, since named access does not apply to inputs.
      input: ["type", "checked", "name"],
    },
    exclusiveFilter: (frame) =>
      frame.tag === "input" &&
      !CSS_ONLY_INPUT_TYPES.has(frame.attribs.type ?? ""),
    allowedSchemes: ["https", "mailto", "tel"],
    allowProtocolRelative: false,
    transformTags: {
      a: (tagName, attribs) => {
        if (attribs.target === "_blank") {
          const rel = new Set((attribs.rel ?? "").split(/\s+/).filter(Boolean));
          rel.add("noopener");
          rel.add("noreferrer");
          attribs.rel = [...rel].join(" ");
        }
        return { tagName, attribs };
      },
    },
  });
}
